import {
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { useForm } from 'react-hook-form';

import { AppError } from '../../../framework/models/errors';
import { TypeUtils } from '../../../framework/utils/UtilityTypes';
import AttachedFiles from '../../components/AttachedFiles';
import { DownloadButton, ViewButton } from '../../components/StyledButton';
import { useFormItems } from '../../hooks/formItems';
import { useGetMatterSummary } from '../../hooks/queries/matter';
import useToastHook from '../../hooks/ToastHook';
import { EngeneFileObject } from '../../models/EngeneFileObject';
import { FileKind } from '../../models/gen/fileKind';
import { MatterSummary } from '../../models/gen/matterSummary';
import { DtMatter } from '../../models/schema';
import { Formatter } from '../../utils/FormatUtils';
import { useSizeContext } from '../SizeProvider';
import { ImageModal } from './Image';

export type MatterDetailModalState = {
  /**
   * 現場ID
   */
  matterId: DtMatter['id'];
};

type MatterDetailFormState = Pick<
  MatterSummary,
  'customerName' | 'station' | 'nameAccountant' | 'nameSales' | 'matterName'
> & { term?: string };

/**
 * 現場詳細ダイアログフック
 * @returns
 */
export const useMatterDetailModal = () => {
  const [state, setState] = React.useState<MatterDetailModalState | null>(null);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedFile, setSelectedFile] = React.useState<EngeneFileObject>();
  const {
    isOpen: isOpenImage,
    onClose: onCloseImage,
    onOpen: onOpenImage,
  } = useDisclosure();
  const form = useForm<MatterDetailFormState>();
  const { FormText } = useFormItems(form);
  const sizes = useSizeContext();

  const { data, error, isError, isLoading } = useGetMatterSummary({
    id: state?.matterId,
  });

  React.useEffect(() => {
    if (!isLoading && !TypeUtils.isNil(data)) {
      form.reset({
        ...data?.result,
        term: `${data?.result?.workDateFrom} - ${data?.result?.workDateTo}`,
      });
    }
  }, [isLoading, data]);

  const { toast } = useToastHook();
  React.useEffect(() => {
    if (isError) {
      toast(
        error instanceof AppError ? error : '現場サマリの取得に失敗しました'
      );
    }
  }, [isError]);

  const handleClose = () => {
    onClose();
    setState(null);
  };

  const handleOpen = (initialState?: MatterDetailModalState) => {
    setState(initialState || null);
    onOpen();
  };

  /**
   * 現場詳細ダイアログ
   * @param props -
   * @returns
   */
  const MatterDetailModal: React.FunctionComponent = () => (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={sizes.fontSize.modalHeader}>
          現場詳細
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize={sizes.fontSize.modalBody} paddingBottom="24px">
          <Skeleton isLoaded={!isLoading} width="100%">
            <FormText
              name="matterName"
              viewNode={({ field }) => (
                <Text fontWeight="bold">{field.value}</Text>
              )}
            />
            <FormText
              name="customerName"
              viewNode={({ field }) => <Text>依頼元: {field.value}</Text>}
            />
            <VStack spacing={2} marginTop={2} marginBottom={0}>
              <FormText label="作業期間" name="term" />
              <FormText label="最寄駅" name="station" />
              <FormText label="営業担当" name="nameSales" />
            </VStack>
            <Divider
              hidden={TypeUtils.isNil(data?.attachedFileNames)}
              paddingY={2}
            />
            <Text
              fontSize={sizes.fontSize.modalBodyTitle}
              hidden={TypeUtils.isNil(data?.attachedFileNames)}
            >
              添付ファイル
            </Text>
            {data?.attachedFileNames?.map((attachFile, idx) => (
              <HStack key={idx}>
                <Text>{attachFile}</Text>
                <ViewButton
                  fileName={attachFile}
                  onOpenImageModal={(s) => {
                    setSelectedFile(s);
                    onOpenImage();
                  }}
                  query={{
                    mode: FileKind.Matter,
                    id: state?.matterId,
                    filename: attachFile,
                  }}
                />
                <DownloadButton
                  fileName={attachFile}
                  query={{
                    mode: FileKind.Matter,
                    id: state?.matterId,
                    filename: attachFile,
                  }}
                />
              </HStack>
            ))}
          </Skeleton>
        </ModalBody>
      </ModalContent>
      <ImageModal
        file={selectedFile}
        isOpen={isOpenImage}
        onClose={() => {
          setSelectedFile(undefined);
          onCloseImage();
        }}
      />
    </Modal>
  );

  return { MatterDetailModal, onClose: handleClose, onOpen: handleOpen };
};

type MatterDetailModal = {
  isOpen?: boolean;
  matterId?: number;
  onClose: Runnable;
};

/**
 * 現場詳細ダイアログ
 * @param props -
 * @returns
 */
export const MatterDetailModal: React.FunctionComponent<MatterDetailModal> = ({
  isOpen,
  matterId,
  onClose,
}) => {
  const sizes = useSizeContext();
  const form = useForm<MatterDetailFormState>();
  const { FormText } = useFormItems(form);

  const { data, error, isError, isLoading, isFetched } = useGetMatterSummary({
    id: matterId,
  });
  const { toast } = useToastHook();
  React.useEffect(() => {
    if (!isLoading && isFetched) {
      form.reset({
        ...data?.result,
        term: `${Formatter.dateOfWeekString(
          data?.result?.workDateFrom,
          '―'
        )} ～ ${Formatter.dateOfWeekString(data?.result?.workDateTo, '―')}`,
      });
    }
  }, [isLoading, data]);
  React.useEffect(() => {
    if (isError) {
      toast(
        error instanceof AppError ? error : '現場サマリの取得に失敗しました'
      );
    }
  }, [isError]);

  return (
    <Modal isOpen={!!isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={sizes.fontSize.modalHeader}>
          現場詳細
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize={sizes.fontSize.modalBody} paddingBottom="24px">
          <Skeleton isLoaded={!isLoading} width="100%">
            <FormText
              name="matterName"
              viewNode={({ field }) => (
                <Text fontWeight="bold">{field.value}</Text>
              )}
            />
            <FormText
              name="customerName"
              viewNode={({ field }) => <Text>依頼元: {field.value}</Text>}
            />
            <VStack spacing={2} marginTop={2} marginBottom={0}>
              <FormText label="作業期間" name="term" />
              <FormText label="最寄駅" name="station" />
              <FormText label="営業担当" name="nameSales" />
              <Divider
                hidden={TypeUtils.isNil(data?.attachedFileNames)}
                paddingY={2}
              />
            </VStack>
            <AttachedFiles
              fileKind={FileKind.Matter}
              files={data?.attachedFileNames}
              id={matterId}
            />
          </Skeleton>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
